// The conversation's right-hand panel (the Mac's channel-info sheet and the four sheets behind it,
// as ONE panel with four sections): Información | Miembros | Fijados | Archivos. Ported from
// ios/Sources/Views/{ChannelInfoSheet,ChannelMembersSheet,ChannelNotificationsSheet,
// ChannelSettingsSheet,ChannelFilesSheet}.swift. Local-first: every section paints from the store
// (or the last answer it had) on the first frame, and the network refreshes behind it.
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { t } from '../../../i18n/i18n';
import { Segmented, SidePanelFrame } from '../../kit/Kit';
import { useLive } from '../../hooks';
import { conversationName, tabsFor, typeIcon, typeLabel, type PanelTab } from './panelModel';
import { InfoTab } from './InfoTab';
import { MembersTab } from './MembersTab';
import { PinnedTab } from './PinnedTab';
import { FilesTab } from './FilesTab';
import './channelPanel.css';

/** The right-hand panel for one conversation; `tab` picks the section (and switches an open panel). */
export function ChannelInfoPanel(props: { channelId: string; tab?: PanelTab; width: number; onClose(): void }) {
  // A different conversation is a different panel: no section state may leak across.
  return <PanelBody key={props.channelId} {...props} />;
}

const SNAPSHOT_TABLES = ['channels', 'channelMembers', 'categories', 'syncState', 'users', 'channelParticipants', 'userStatuses'] as const;

function PanelBody({ channelId, tab, width, onClose }: { channelId: string; tab?: PanelTab; width: number; onClose(): void }) {
  const [current, setCurrent] = useState<PanelTab>(tab ?? 'info');
  // The header's pin button on an already-open panel switches the section.
  useEffect(() => {
    if (tab) setCurrent(tab);
  }, [tab]);

  const { value: snap } = useLive(() => data.call('channelPanelSnapshot', channelId), [...SNAPSHOT_TABLES], [channelId]);
  useEffect(() => {
    void data.call('channelPanelRefresh', channelId).catch(() => undefined);
  }, [channelId]);

  const channel = snap?.channel ?? null;
  const tabs = tabsFor(channel?.type);
  const shown: PanelTab = tabs.includes(current) ? current : 'info';
  const name = useMemo(() => (snap ? conversationName(snap) : ''), [snap]);
  const labels: Record<PanelTab, string> = {
    info: t('channel.info.title'),
    members: t('people.members'),
    pinned: t('web.channelPanel.tab.pinned'),
    files: t('channel.files.title'),
  };

  return (
    <SidePanelFrame icon={typeIcon(channel?.type ?? 'O')} title={name || t('channel.info.title')} subtitle={channel ? typeLabel(channel) : undefined} onClose={onClose} width={width}>
      <div className="cp-tabs">
        <Segmented<PanelTab> value={shown} options={tabs.map((v) => ({ value: v, label: labels[v] }))} onChange={setCurrent} />
      </div>
      {snap && channel ? (
        <div className="cp-section" role="tabpanel" aria-label={labels[shown]}>
          {shown === 'info' ? <InfoTab snap={snap} channel={channel} name={name} onClose={onClose} onOpenTab={setCurrent} /> : null}
          {shown === 'members' ? <MembersTab snap={snap} channel={channel} /> : null}
          {shown === 'pinned' ? <PinnedTab channel={channel} name={name} /> : null}
          {shown === 'files' ? <FilesTab channel={channel} /> : null}
        </div>
      ) : null}
    </SidePanelFrame>
  );
}
